import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Play, Sparkle, FilmStrip } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { VideoGrid } from '@/components/VideoGrid';
import { StoryboardView } from '@/components/StoryboardView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VideoCreation = () => {
  const [videos, setVideos] = useState([]);
  const [storyboards, setStoryboards] = useState([]);
  const [selectedStoryboard, setSelectedStoryboard] = useState(null);
  const [activeView, setActiveView] = useState('videos');
  const [showNewVideo, setShowNewVideo] = useState(false);
  const [showNewStoryboard, setShowNewStoryboard] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newVideo, setNewVideo] = useState({ prompt: '', model: 'sora-2', size: '1280x720', duration: 4 });
  const [newStoryboard, setNewStoryboard] = useState({ title: '', description: '' });

  const loadVideos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading videos:', error);
    }
  }, []);

  const loadStoryboards = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/storyboards`);
      setStoryboards(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading storyboards:', error);
    }
  }, []);

  useEffect(() => {
    loadVideos();
    loadStoryboards();
  }, [loadVideos, loadStoryboards]);

  const generateVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.prompt.trim()) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/videos/generate`, newVideo);
      const videoId = response.data.video_id;
      toast.success('Video generation started! This takes 2-5 minutes...');
      setShowNewVideo(false);

      // Poll for completion
      const poll = async (attempt) => {
        if (attempt >= 90) {
          toast.error('Video generation timed out. Check the Videos tab later.');
          setGenerating(false);
          return;
        }
        try {
          const status = await axios.get(`${API}/videos/job/${videoId}`);
          if (status.data.status === 'completed') {
            toast.success('Video ready!');
            setNewVideo({ prompt: '', model: 'sora-2', size: '1280x720', duration: 4 });
            loadVideos();
            setGenerating(false);
            return;
          }
          if (status.data.status === 'failed') {
            toast.error('Video generation failed. Try again.');
            setGenerating(false);
            return;
          }
          setTimeout(() => poll(attempt + 1), 5000);
        } catch {
          setTimeout(() => poll(attempt + 1), 5000);
        }
      };
      poll(0);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Rate limit reached');
      } else {
        toast.error('Failed to start video generation');
      }
      setGenerating(false);
    }
  };

  const createStoryboard = async (e) => {
    e.preventDefault();
    if (!newStoryboard.title.trim()) return;
    try {
      const response = await axios.post(`${API}/storyboards`, newStoryboard);
      toast.success('Storyboard created!');
      setNewStoryboard({ title: '', description: '' });
      setShowNewStoryboard(false);
      loadStoryboards();
      setSelectedStoryboard(response.data);
      setActiveView('storyboards');
    } catch (error) {
      toast.error('Failed to create storyboard');
    }
  };

  const addScene = async (sceneData) => {
    if (!selectedStoryboard) return;
    setGenerating(true);
    try {
      await axios.post(
        `${API}/storyboards/${selectedStoryboard.id}/scenes`,
        { ...sceneData, storyboard_id: selectedStoryboard.id }
      );
      toast.success('Scene generated!');
      const sbResponse = await axios.get(`${API}/storyboards/${selectedStoryboard.id}`);
      setSelectedStoryboard(sbResponse.data);
      loadStoryboards();
    } catch (error) {
      toast.error('Failed to generate scene');
    } finally {
      setGenerating(false);
    }
  };

  const downloadVideo = async (videoId) => {
    try {
      const response = await axios.get(`${API}/videos/${videoId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `busybodies_video_${videoId}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Video downloaded!');
    } catch (error) {
      toast.error('Failed to download video');
    }
  };

  return (
    <div className='h-full flex flex-col bg-black' data-testid='video-creation-panel'>
      <div className='border-b border-border p-6 bg-surface'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            <Video size={28} weight='duotone' className='text-foreground' />
            <div>
              <h2 className='text-2xl font-bold tracking-tight' data-testid='videos-header-title'>AI Video Creation</h2>
              <p className='text-sm text-muted-foreground'>Create 3D lifelike videos & full movies with AI</p>
            </div>
          </div>
          <div className='flex gap-2'>
            <Button onClick={() => setShowNewVideo(!showNewVideo)} className='bg-white text-black hover:bg-gray-200' data-testid='new-video-button'>
              <Sparkle size={20} weight='fill' className='mr-2' /> Generate Video
            </Button>
            <Button onClick={() => setShowNewStoryboard(!showNewStoryboard)} className='bg-purple-600 text-white hover:bg-purple-700' data-testid='new-storyboard-button'>
              <FilmStrip size={20} weight='fill' className='mr-2' /> New Movie
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {showNewVideo && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='mt-4 p-4 border border-border bg-surface' data-testid='new-video-form'>
              <form onSubmit={generateVideo} className='space-y-3'>
                <Textarea value={newVideo.prompt} onChange={(e) => setNewVideo({ ...newVideo, prompt: e.target.value })} placeholder='Describe your video...' className='bg-transparent border-border min-h-[100px]' data-testid='video-prompt-input' />
                <div className='grid grid-cols-3 gap-2'>
                  <select value={newVideo.size} onChange={(e) => setNewVideo({ ...newVideo, size: e.target.value })} className='bg-transparent border border-border p-2 text-sm' data-testid='video-size-select'>
                    <option value='1280x720' className='bg-black'>HD (16:9)</option>
                    <option value='1792x1024' className='bg-black'>Widescreen</option>
                    <option value='1024x1792' className='bg-black'>Portrait</option>
                    <option value='1024x1024' className='bg-black'>Square</option>
                  </select>
                  <select value={newVideo.duration} onChange={(e) => setNewVideo({ ...newVideo, duration: parseInt(e.target.value) })} className='bg-transparent border border-border p-2 text-sm' data-testid='video-duration-select'>
                    <option value={4} className='bg-black'>4 seconds</option>
                    <option value={8} className='bg-black'>8 seconds</option>
                    <option value={12} className='bg-black'>12 seconds</option>
                  </select>
                  <select value={newVideo.model} onChange={(e) => setNewVideo({ ...newVideo, model: e.target.value })} className='bg-transparent border border-border p-2 text-sm' data-testid='video-model-select'>
                    <option value='sora-2' className='bg-black'>Sora 2</option>
                    <option value='sora-2-pro' className='bg-black'>Sora 2 Pro</option>
                  </select>
                </div>
                <Button type='submit' disabled={generating} className='w-full bg-white text-black hover:bg-gray-200' data-testid='generate-video-button'>
                  {generating ? 'Generating Video (2-5 min)...' : 'Generate Video'}
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showNewStoryboard && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='mt-4 p-4 border border-border bg-surface' data-testid='new-storyboard-form'>
              <form onSubmit={createStoryboard} className='space-y-3'>
                <Input value={newStoryboard.title} onChange={(e) => setNewStoryboard({ ...newStoryboard, title: e.target.value })} placeholder='Movie title' className='bg-transparent border-border' data-testid='storyboard-title-input' />
                <Textarea value={newStoryboard.description} onChange={(e) => setNewStoryboard({ ...newStoryboard, description: e.target.value })} placeholder='Movie description / storyline' className='bg-transparent border-border min-h-[80px]' data-testid='storyboard-description-input' />
                <Button type='submit' className='bg-white text-black hover:bg-gray-200' data-testid='create-storyboard-button'>Create Movie Project</Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        <div className='flex gap-2 mt-4'>
          <Button onClick={() => setActiveView('videos')} variant={activeView === 'videos' ? 'default' : 'outline'} className={activeView === 'videos' ? 'bg-white text-black' : 'border-border'} data-testid='videos-view-button'>
            <Play size={16} className='mr-2' /> Videos ({videos.length})
          </Button>
          <Button onClick={() => setActiveView('storyboards')} variant={activeView === 'storyboards' ? 'default' : 'outline'} className={activeView === 'storyboards' ? 'bg-white text-black' : 'border-border'} data-testid='storyboards-view-button'>
            <FilmStrip size={16} className='mr-2' /> Movies ({storyboards.length})
          </Button>
        </div>
      </div>

      <div className='flex-1 overflow-y-auto p-6'>
        {activeView === 'videos' ? (
          <VideoGrid videos={videos} onDownload={downloadVideo} />
        ) : (
          <StoryboardView
            storyboards={storyboards}
            selectedStoryboard={selectedStoryboard}
            setSelectedStoryboard={setSelectedStoryboard}
            generating={generating}
            onAddScene={addScene}
          />
        )}
      </div>
    </div>
  );
};

export default VideoCreation;
